import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { buttonClass, inputClass } from '@/components/ui';
import type { SupplierItemHit } from '@/lib/stock-types';

/**
 * ADD ITEMS to a purchase order. Spec 012 FR-1203 and spec 013 FR-1325.
 *
 * Search by our code, product, colour, size or the supplier's code. The
 * supplier's own items come first, and only they show until "Show all items"
 * is ticked, so a buyer ordering from AS Colour does not wade through Gildan.
 * A supplier with no items yet shows everything, and says so. A garment shows
 * product, colour and size in their own columns.
 *
 * `onAdd` gets the hit. Items already on the order are marked, not hidden, so
 * the buyer sees why the button is gone.
 */
export function ItemPicker({
  supplierId,
  onOrder,
  onAdd,
}: {
  supplierId: string;
  /** Item ids already on the order. */
  onOrder: string[];
  onAdd: (hit: SupplierItemHit) => void;
}) {
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);

  // A short pause, so a search runs once per word and not once per letter.
  useEffect(() => {
    const t = setTimeout(() => setQ(text.trim()), 250);
    return () => clearTimeout(t);
  }, [text]);

  const hits = useQuery({
    queryKey: ['admin', 'supplier-item-search', supplierId, q],
    queryFn: async (): Promise<SupplierItemHit[]> => {
      const res = await fetch(
        `/api/suppliers/${supplierId}/item-search?q=${encodeURIComponent(q)}&limit=50`,
        { credentials: 'same-origin' },
      );
      if (!res.ok) throw new Error(`Search failed (${res.status})`);
      return ((await res.json()) as { rows: SupplierItemHit[] }).rows;
    },
    enabled: Boolean(supplierId),
    staleTime: 30_000,
  });

  const rows = hits.data ?? [];
  const linked = rows.filter((r) => r.linked);
  // Without "Show all items", the supplier's own. When it has none in the
  // answer, everything, because an empty list helps nobody.
  const shown = all || linked.length === 0 ? rows : linked;
  const hidden = rows.length - shown.length;

  return (
    <div className="rounded-[10px] border border-line p-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex-1 text-[13px]" htmlFor={`picker-${supplierId}`}>
          <span className="font-semibold">Add items</span>
          <input
            id={`picker-${supplierId}`}
            className={`${inputClass} mt-1 w-full`}
            placeholder="Code, product, colour, size or the supplier's code"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <label className="flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
          Show all items
        </label>
      </div>

      {hits.isError && <p className="mt-2 text-[13px] text-danger">The search did not work. Try again.</p>}
      {!hits.isFetching && q === '' && rows.length === 0 && (
        <p className="mt-2 text-[13px] text-muted">
          This supplier has no items on its list yet. Type to search all items.
        </p>
      )}
      {!hits.isFetching && q !== '' && rows.length === 0 && (
        <p className="mt-2 text-[13px] text-muted">No item matches that.</p>
      )}

      {shown.length > 0 && (
        <div className="mt-3 max-h-[320px] overflow-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[12px] text-muted">
                <th className="py-1 pr-2 font-semibold">Our code</th>
                <th className="py-1 pr-2 font-semibold">Product</th>
                <th className="py-1 pr-2 font-semibold">Colour</th>
                <th className="py-1 pr-2 font-semibold">Size</th>
                <th className="py-1 pr-2 font-semibold">Supplier code</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const there = onOrder.includes(r.item_id);
                return (
                  <tr key={r.item_id} className="border-t border-line">
                    <td className="py-1 pr-2 font-mono">{r.code}</td>
                    <td className="py-1 pr-2">{r.name}</td>
                    <td className="py-1 pr-2">{r.colour ?? ''}</td>
                    <td className="py-1 pr-2">{r.size ?? ''}</td>
                    <td className="py-1 pr-2 font-mono text-muted">
                      {r.supplier_code ?? (r.linked ? '' : 'Not on its list')}
                    </td>
                    <td className="py-1 text-right">
                      {there
                        ? <span className="text-muted">On the order</span>
                        : (
                          <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => onAdd(r)}>
                            Add
                          </button>
                        )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {hidden > 0 && (
        <p className="mt-2 text-[12px] text-muted">
          {hidden} more {hidden === 1 ? 'item matches' : 'items match'} that this supplier does not sell.
          Tick Show all items to see {hidden === 1 ? 'it' : 'them'}.
        </p>
      )}
    </div>
  );
}

/** Where a suggested price came from. FR-1261. */
export function priceBasisLabel(basis: 'last_paid' | 'last_ordered' | null | undefined, at: string | null | undefined) {
  if (!basis || !at) return null;
  const day = new Date(at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  return basis === 'last_paid' ? `Last paid ${day}` : `Last ordered ${day}`;
}

/** Dollars typed by a person, as whole cents, or null for blank. */
export function centsFromText(text: string): number | null | 'bad' {
  const t = text.trim().replace(/^\$/, '');
  if (t === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return 'bad';
  return Math.round(Number(t) * 100);
}
